import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Mic, FileText, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";

/**
 * Global search — top-bar input that queries `/api/orders/search-transcripts`
 * so any older voice-note remark or order comment is one type-away.
 *
 * Debounced 250ms, results collapse into a floating card, click-through opens
 * the order detail. Role scoping is enforced server-side; a sales rep only
 * ever sees hits inside orders they created.
 */
export default function GlobalSearch() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setItems([]); return; }
    let ignore = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/orders/search-transcripts", { params: { q: term } });
        if (!ignore) setItems(data.items || []);
      } catch (err) { if (!ignore) setItems([]); }
      finally { if (!ignore) setLoading(false); }
    }, 250);
    return () => { ignore = true; clearTimeout(t); };
  }, [q]);

  // Close on outside click.
  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (id) => {
    setOpen(false);
    setQ("");
    setItems([]);
    nav(`/orders/${id}`);
  };

  return (
    <div className="hidden md:flex items-center gap-2 max-w-md flex-1 mx-2 lg:mx-4" ref={boxRef}>
      <div className="relative w-full">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5A50]" />
        <Input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search orders + voice-note transcripts…"
          className="pl-9 rounded-full bg-white border-[#E4DFCF]"
          data-testid="global-search"
        />
        {loading && (
          <Loader2
            size={14}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A5A50] animate-spin"
          />
        )}
        {open && q.trim().length >= 2 && (
          <div
            className="absolute z-40 left-0 right-0 mt-2 rounded-xl border border-[#E4DFCF] bg-white shadow-xl max-h-96 overflow-y-auto"
            data-testid="global-search-results"
          >
            {!loading && items.length === 0 && (
              <div className="p-4 text-sm text-[#4A5A50]">No matches — voice notes are auto-transcribed after saving.</div>
            )}
            {items.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => go(r.id)}
                className="w-full text-left px-3 py-2.5 hover:bg-[#FAF7EF] border-b border-[#F5F0E1] last:border-0"
                data-testid={`global-search-hit-${r.id}`}
              >
                <div className="flex items-center gap-2">
                  {r.source === "voice"
                    ? <Mic size={12} className="text-[#0F5E39] flex-shrink-0"/>
                    : <FileText size={12} className="text-[#4A5A50] flex-shrink-0"/>}
                  <div className="text-sm text-[#0F1E15] flex-1 truncate">
                    {r.reference_number ? `#${r.reference_number}` : r.id.slice(0, 8)}
                    {r.client_name ? <span className="text-[#4A5A50]"> · {r.client_name}</span> : null}
                  </div>
                  {r.status ? (
                    <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#F1EDDF] text-[#4A5A50] flex-shrink-0">
                      {r.status}
                    </span>
                  ) : null}
                </div>
                {r.snippet && (
                  <div className="text-xs text-[#4A5A50] italic mt-1 pl-4">"{r.snippet}"</div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
